import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../lib/api';
import { LayoutDashboard, Users, FileText, BookOpenCheck, Shield, Sparkles, Inbox, Mail, BarChart3, ExternalLink, LogOut, Building2, ArrowLeft, MapPin, Package, Contact, Clapperboard } from 'lucide-react';

const NAV = [
  { to: '/admin', label: 'Tableau de bord', icon: LayoutDashboard, exact: true },
  { to: '/admin/stats', label: 'Statistiques', icon: BarChart3 },
  { to: '/admin/users', label: 'Utilisateurs', icon: Users, badge: 'pending' },
  { to: '/admin/demandes', label: 'Demandes', icon: Inbox, badge: 'demandes' },
  { to: '/admin/inbox', label: 'Boîte mail', icon: Mail, badge: 'inbox' },
  { to: '/admin/articles', label: 'Articles', icon: FileText },
  { to: '/admin/annuaire', label: 'Annuaire', icon: MapPin },
  { to: '/admin/export', label: 'Export packs', icon: Package },
  { to: '/admin/livret', label: 'Suivi livret', icon: BookOpenCheck },
  { to: '/admin/actu', label: 'Actu & Fun', icon: Sparkles },
  { to: '/admin/shorts', label: 'Shorts communauté', icon: Clapperboard },
  { to: '/vcard', label: 'Carte de visite', icon: Contact },
  { to: '/secretariat', label: 'Secrétariat', icon: Building2 },
];

export default function AdminShell({ children }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [badges, setBadges] = useState({ pending: 0, demandes: 0, inbox: 0 });

  useEffect(() => {
    api.adminStats()
      .then((s) => setBadges({ pending: s?.users?.pending || 0, demandes: s?.demandes?.new || 0, inbox: s?.inbox?.unread || 0 }))
      .catch(() => {});
  }, [pathname]);

  const onLogout = () => { logout(); navigate('/'); };

  return (
    <div className="min-h-screen bg-[#0a0a0b] flex flex-col lg:flex-row" data-testid="admin-shell">
      {/* Sidebar */}
      <aside className="lg:w-60 lg:min-h-screen shrink-0 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0d0d0e] flex lg:flex-col">
        <div className="hidden lg:flex items-center gap-2.5 px-5 h-16 border-b border-white/10">
          <div className="w-9 h-9 rounded-xl bg-red-600 text-white flex items-center justify-center shadow-lg shadow-red-900/40">
            <Shield size={16} />
          </div>
          <div>
            <div className="text-[9.5px] uppercase tracking-[0.22em] text-red-400 font-bold font-mono-spec">codevoiture.fr</div>
            <div className="text-white text-[14px] font-semibold leading-tight">Back-office</div>
          </div>
        </div>
        <nav className="flex lg:flex-col gap-1 p-2 lg:p-3 overflow-x-auto lg:overflow-visible flex-1 items-center lg:items-stretch">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = n.exact ? pathname === n.to : pathname.startsWith(n.to);
            const count = n.badge ? badges[n.badge] : 0;
            return (
              <Link
                key={n.to}
                to={n.to}
                data-testid={`admin-side-${n.to.replace(/\//g, '-')}`}
                className={`flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold whitespace-nowrap transition ${
                  active ? 'bg-red-600 text-white shadow-md shadow-red-900/40' : 'text-white/65 hover:bg-white/5 hover:text-white'
                }`}
              >
                <Icon size={15} className="shrink-0" />
                <span className="flex-1">{n.label}</span>
                {count > 0 && (
                  <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10.5px] font-bold inline-flex items-center justify-center ${active ? 'bg-white text-red-600' : 'bg-red-600 text-white'}`}>{count}</span>
                )}
              </Link>
            );
          })}
        </nav>
        <div className="hidden lg:block p-3 border-t border-white/10 space-y-1">
          <Link to="/" data-testid="admin-back-to-site" className="flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold text-white/65 hover:bg-white/5 hover:text-white transition">
            <ExternalLink size={14} /> Voir le site
          </Link>
          <button onClick={onLogout} data-testid="admin-logout" className="w-full flex items-center gap-2.5 px-3.5 h-10 rounded-xl text-[13px] font-semibold text-white/65 hover:bg-red-950/40 hover:text-red-300 transition">
            <LogOut size={14} /> Déconnexion
          </button>
          {user && <div className="px-3.5 pt-2 pb-1 text-white/35 text-[11px] font-mono-spec truncate">{user.email}</div>}
        </div>
      </aside>

      {/* Contenu */}
      <div className="flex-1 min-w-0">
        <header className="h-12 lg:h-16 border-b border-white/10 bg-[#0d0d0e]/60 backdrop-blur flex items-center justify-between px-4 lg:px-8">
          <div className="flex items-center gap-2 min-w-0">
            {pathname !== '/admin' && (
              <button
                onClick={() => (window.history.length > 1 ? navigate(-1) : navigate('/admin'))}
                data-testid="admin-mobile-back"
                aria-label="Retour"
                className="lg:hidden shrink-0 -ml-1 w-9 h-9 rounded-lg border border-white/10 bg-white/5 text-white/80 active:scale-95 inline-flex items-center justify-center"
              >
                <ArrowLeft size={16} />
              </button>
            )}
            <div className="text-white/45 text-[12px] font-mono-spec uppercase tracking-[0.2em] truncate">Administration</div>
          </div>
          <div className="flex items-center gap-2 lg:hidden">
            <Link to="/" className="text-white/60 text-[12px] inline-flex items-center gap-1"><ExternalLink size={12} /> Site</Link>
            <button onClick={onLogout} className="text-red-300 text-[12px] inline-flex items-center gap-1"><LogOut size={12} /> Quitter</button>
          </div>
        </header>
        <main className="px-4 lg:px-8 py-6 lg:py-8">{children}</main>
      </div>
    </div>
  );
}
